import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, mediaUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Vote, CheckCircle2, AlertTriangle, ArrowLeft, Copy, Award, Users } from "lucide-react";
import { toast } from "sonner";
import MemberPicker from "../components/MemberPicker";

/**
 * Voter ballot flow:
 *   Step 1: pick a choice per office (radio + optional write-in field)
 *   Step 2: review — read-only summary of picks + required certification checkbox
 *   Step 3: confirmation — display confirmation code, prevent resubmission
 *
 * If the election is CERTIFIED, show final results instead of the ballot.
 */
export default function ElectionDetail() {
    const { id } = useParams();
    const [e, setE] = useState(null);
    const [err, setErr] = useState("");
    const [step, setStep] = useState("ballot");    // ballot | review | confirmation | results
    const [selections, setSelections] = useState({}); // { officeId: { choice_type, candidate_id, writein_name } }
    const [certified, setCertified] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [confirmation, setConfirmation] = useState("");
    const [results, setResults] = useState(null);
    const [members, setMembers] = useState([]);   // active AOP members for the write-in picker

    useEffect(() => {
        api.get(`/elections/${id}`)
            .then(({ data }) => {
                setE(data);
                if (data.status === "certified") {
                    api.get(`/elections/${id}/results`).then(({ data: r }) => setResults(r)).catch(() => {});
                    setStep("results");
                } else if (data.has_voted) {
                    setStep("already_voted");
                }
            })
            .catch((ex) => setErr(formatApiError(ex?.response?.data?.detail) || "Election not found."));
        // Best-effort — voters see the searchable member list for write-ins.
        api.get(`/elections/${id}/eligible-members`).then(({ data }) => setMembers(data || [])).catch(() => setMembers([]));
    }, [id]);

    if (err) return <ErrorBox message={err} />;
    if (!e) return <div className="max-w-3xl mx-auto px-6 py-12 text-muted-foreground">Loading…</div>;

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="election-detail">
            <Link to="/elections" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-4">
                <ArrowLeft className="h-4 w-4" /> All elections
            </Link>
            <Header e={e} />
            {step === "results" && <ResultsView results={results} election={e} />}
            {step === "already_voted" && <AlreadyVoted />}
            {step === "ballot" && ["open", "runoff_open"].includes(e.status) && e.eligible && (
                <BallotView e={e} selections={selections} setSelections={setSelections} members={members}
                    onReview={() => setStep("review")} />
            )}
            {step === "ballot" && (!["open", "runoff_open"].includes(e.status) || !e.eligible) && (
                <NotVotableView e={e} />
            )}
            {step === "review" && (
                <ReviewView
                    e={e}
                    selections={selections}
                    certified={certified}
                    setCertified={setCertified}
                    onBack={() => setStep("ballot")}
                    submitting={submitting}
                    onSubmit={async () => {
                        setSubmitting(true);
                        try {
                            const choices = e.offices.map((o) => {
                                const s = selections[o.id] || { choice_type: "blank" };
                                return { office_id: o.id, ...s };
                            });
                            const { data } = await api.post(`/elections/${id}/ballot`, { choices, certified });
                            setConfirmation(data.confirmation_code);
                            setStep("confirmation");
                            toast.success("Ballot submitted");
                        } catch (ex) {
                            toast.error(formatApiError(ex?.response?.data?.detail) || "Could not submit your ballot.");
                        } finally { setSubmitting(false); }
                    }}
                />
            )}
            {step === "confirmation" && <ConfirmationView code={confirmation} />}
        </div>
    );
}

function Header({ e }) {
    return (
        <div className="mb-6">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight" data-testid="election-name">{e.name}</h1>
            {e.description && <p className="text-muted-foreground mt-2">{e.description}</p>}
            <div className="mt-3 text-xs text-muted-foreground flex flex-wrap gap-3">
                <span>{new Date(e.starts_at).toLocaleString()} → {new Date(e.closes_at).toLocaleString()}</span>
                <span>· {e.offices?.length || 0} office{(e.offices?.length || 0) === 1 ? "" : "s"}</span>
                <span className="uppercase tracking-wider font-semibold text-primary">{e.status.replace(/_/g, " ")}</span>
            </div>
        </div>
    );
}

function BallotView({ e, selections, setSelections, onReview, members }) {
    const allAnswered = useMemo(() => (e.offices || []).every((o) => selections[o.id] && selections[o.id].choice_type), [e.offices, selections]);
    return (
        <div className="space-y-6" data-testid="ballot-view">
            {e.offices.map((o) => (
                <OfficeCard key={o.id} office={o} value={selections[o.id]} members={members}
                    onChange={(next) => setSelections((s) => ({ ...s, [o.id]: next }))} />
            ))}
            <div className="sticky bottom-4 z-10 flex justify-end">
                <Button
                    onClick={onReview}
                    disabled={!allAnswered}
                    className="rounded-full px-6"
                    data-testid="ballot-review-btn"
                >Review my ballot</Button>
            </div>
        </div>
    );
}

function OfficeCard({ office, value, onChange, members }) {
    const candidates = (office.candidates || []).filter((c) => !c.is_writein && c.status === "qualified");
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid={`office-${office.id}`}>
            <div className="font-heading text-lg font-bold">{office.title}</div>
            {office.description && <p className="text-sm text-muted-foreground mt-1">{office.description}</p>}
            <div className="mt-4 space-y-2">
                <RadioGroup
                    value={value?.candidate_id || (value?.choice_type === "writein" ? "__writein" : (value?.choice_type === "blank" ? "__blank" : ""))}
                    onValueChange={(v) => {
                        if (v === "__writein") onChange({ choice_type: "writein", writein_name: value?.writein_name || "" });
                        else if (v === "__blank") onChange({ choice_type: "blank" });
                        else onChange({ choice_type: "candidate", candidate_id: v });
                    }}
                >
                    {candidates.map((c) => (
                        <label key={c.id} className="flex items-start gap-3 rounded-xl border border-slate-200 hover:border-primary/50 p-3 cursor-pointer transition-colors" data-testid={`candidate-${c.id}`}>
                            <RadioGroupItem value={c.id} className="mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3">
                                    {c.photo_url && <img src={mediaUrl(c.photo_url)} alt="" className="h-10 w-10 rounded-full object-cover" />}
                                    <div className="font-semibold">{c.name}</div>
                                </div>
                                {c.statement && <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{c.statement}</p>}
                            </div>
                        </label>
                    ))}
                    {office.allow_writeins && (
                        <label className="flex items-start gap-3 rounded-xl border border-dashed border-slate-300 hover:border-primary/50 p-3 cursor-pointer">
                            <RadioGroupItem value="__writein" className="mt-0.5" />
                            <div className="flex-1">
                                <div className="font-semibold">Write-in</div>
                                <p className="text-xs text-muted-foreground">Search for an active AOP member, or type any name.</p>
                                {value?.choice_type === "writein" && (
                                    <div className="mt-2">
                                        <MemberPicker
                                            options={members || []}
                                            value={value.writein_name}
                                            onChange={(m) => onChange({ choice_type: "writein", writein_name: m?.name || "" })}
                                            placeholder="Search AOP members or type any name…"
                                            testid={`writein-picker-${office.id}`}
                                            allowFreeText
                                            freeTextLabel="Use this exact name"
                                        />
                                    </div>
                                )}
                            </div>
                        </label>
                    )}
                    <label className="flex items-start gap-3 rounded-xl border border-slate-200 hover:border-primary/50 p-3 cursor-pointer">
                        <RadioGroupItem value="__blank" className="mt-0.5" />
                        <div>
                            <div className="font-semibold">Abstain</div>
                            <p className="text-xs text-muted-foreground">Leave this office blank on your ballot.</p>
                        </div>
                    </label>
                </RadioGroup>
            </div>
        </div>
    );
}

function ReviewView({ e, selections, certified, setCertified, onBack, onSubmit, submitting }) {
    return (
        <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-5 sm:p-6" data-testid="ballot-review">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Vote className="h-5 w-5 text-primary" /> Review your ballot</h2>
            <p className="text-sm text-muted-foreground mt-1">Your selections will be recorded anonymously. You cannot change them after submission.</p>
            <div className="mt-4 space-y-3">
                {e.offices.map((o) => {
                    const s = selections[o.id] || { choice_type: "blank" };
                    let name = "— Abstain —";
                    if (s.choice_type === "candidate") {
                        const c = (o.candidates || []).find((cc) => cc.id === s.candidate_id);
                        name = c?.name || "(unknown)";
                    } else if (s.choice_type === "writein") {
                        name = `Write-in: ${s.writein_name || "(blank)"}`;
                    }
                    return (
                        <div key={o.id} className="rounded-xl border border-slate-200 bg-white p-3 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-xs uppercase tracking-wider text-muted-foreground">{o.title}</div>
                                <div className="font-semibold truncate" data-testid={`review-choice-${o.id}`}>{name}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <label className="mt-5 flex items-start gap-3 cursor-pointer" data-testid="certify-checkbox">
                <Checkbox checked={certified} onCheckedChange={(v) => setCertified(!!v)} className="mt-0.5" />
                <span className="text-sm">I certify that I am an eligible AOP member and that I am submitting my own ballot.</span>
            </label>
            <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <Button variant="outline" className="rounded-full" onClick={onBack} disabled={submitting}>Back to ballot</Button>
                <Button className="rounded-full" onClick={onSubmit} disabled={!certified || submitting} data-testid="submit-ballot-btn">
                    {submitting ? "Submitting…" : "Submit ballot"}
                </Button>
            </div>
        </div>
    );
}

function ConfirmationView({ code }) {
    return (
        <div className="rounded-2xl border-2 border-emerald-500/40 bg-emerald-50 p-6 text-center" data-testid="ballot-confirmation">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
            <h2 className="font-heading text-2xl font-bold">Ballot submitted</h2>
            <p className="text-sm text-muted-foreground mt-1">Thank you for participating in the AOP election.</p>
            <div className="mt-4 inline-flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-white px-4 py-3 font-mono text-lg" data-testid="confirmation-code">
                <span>{code}</span>
                <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard.writeText(code); toast.success("Copied"); }}><Copy className="h-4 w-4" /></Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">Save this confirmation code for your records.</p>
            <div className="mt-6">
                <Link to="/elections" className="text-primary font-semibold hover:underline">Back to elections</Link>
            </div>
        </div>
    );
}

function AlreadyVoted() {
    return (
        <div className="rounded-2xl border border-emerald-500/40 bg-emerald-50 p-6" data-testid="ballot-already-voted">
            <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                <div>
                    <div className="font-heading text-lg font-bold">You have already voted</div>
                    <p className="text-sm text-muted-foreground">Your ballot was recorded. Results will be posted after the election is certified.</p>
                </div>
            </div>
        </div>
    );
}

function NotVotableView({ e }) {
    return (
        <div className="rounded-2xl border border-amber-500/40 bg-amber-50 p-6" data-testid="ballot-not-votable">
            <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-600 mt-0.5" />
                <div>
                    <div className="font-heading text-lg font-bold">Not currently open for voting</div>
                    <p className="text-sm text-muted-foreground">
                        This election is <b>{e.status.replace(/_/g, " ")}</b>. Either voting is not yet open, or you are not eligible for this election.
                    </p>
                </div>
            </div>
        </div>
    );
}

function ResultsView({ results, election }) {
    if (!results) return <div className="text-muted-foreground">Loading results…</div>;
    return (
        <div className="space-y-6" data-testid="results-view">
            <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-5">
                <div className="flex items-center gap-2 font-heading text-xl font-bold"><Award className="h-5 w-5 text-primary" /> Certified Results</div>
                <p className="text-xs text-muted-foreground mt-1">
                    Certified {election.certified_at ? new Date(election.certified_at).toLocaleString() : ""} {election.certified_by_name ? `by ${election.certified_by_name}` : ""}
                </p>
            </div>
            {results.offices.map((o) => (
                <OfficeResultCard key={o.office_id} o={o} />
            ))}
        </div>
    );
}

function OfficeResultCard({ o }) {
    const winner = o.outcome.winner_id ? o.tally.candidates.find((c) => c.id === o.outcome.winner_id) : null;
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-heading text-lg font-bold">{o.office_title}</div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        {o.tally.total_ballots} ballot{o.tally.total_ballots === 1 ? "" : "s"} · {o.tally.valid_votes} valid · {o.tally.invalid_votes} invalid
                    </p>
                </div>
                <div className="text-right">
                    <div className={`text-[10px] uppercase tracking-wider font-bold ${o.outcome.kind === "winner" ? "text-emerald-700" : "text-amber-700"}`}>
                        {o.outcome.kind.replace("_", " ")}
                    </div>
                    {winner && <div className="text-sm font-semibold">{winner.name}</div>}
                </div>
            </div>
            {o.outcome.reason && <p className="text-xs text-muted-foreground mt-2 italic">{o.outcome.reason}</p>}
            <div className="mt-4 space-y-2">
                {o.tally.candidates.map((c) => (
                    <div key={c.id} className={`p-3 rounded-xl border ${c.id === o.outcome.winner_id ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white"}`}>
                        <div className="flex items-center justify-between gap-3 text-sm">
                            <div className="font-semibold flex items-center gap-2">
                                {c.name} {c.is_writein && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">(write-in)</span>}
                                {c.status !== "qualified" && <span className="text-[10px] uppercase tracking-wider text-amber-700">({c.status})</span>}
                            </div>
                            <div className="tabular-nums font-mono">{c.votes} · {c.pct_of_valid.toFixed(1)}%</div>
                        </div>
                        <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className={`h-full ${c.id === o.outcome.winner_id ? "bg-emerald-500" : "bg-primary/60"}`} style={{ width: `${Math.min(100, c.pct_of_valid)}%` }} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ErrorBox({ message }) {
    return (
        <div className="max-w-3xl mx-auto px-6 py-12">
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-destructive mt-0.5" />
                <div>
                    <div className="font-heading text-lg font-bold">Could not load election</div>
                    <p className="text-sm text-muted-foreground">{message}</p>
                </div>
            </div>
        </div>
    );
}
